import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import OrgMemberAvatar from "@/components/avatars/OrgMemberAvatar";
import { API_BASE_URL } from "@/lib/apiBase";
import { useAuth } from "@/scripts/AuthContext";
import { BOT_PRESENCE } from "@/lib/presence";
import { cn } from "@/lib/utils";

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

/**
 * Profile panel for Circatron incoming webhook bots (not org members).
 */
export default function WebhookBotProfileModal({
  open,
  onOpenChange,
  orgId,
  webhookId,
  seedUser = null,
}) {
  const { accessToken } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !orgId || !webhookId || !accessToken) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/org/${orgId}/connectors/incoming/${webhookId}/profile`,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );
        if (!cancelled) setProfile(res.data || null);
      } catch (err) {
        if (!cancelled) {
          setError(
            err.response?.data?.message ||
              err.message ||
              "Could not load bot profile"
          );
          setProfile(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, orgId, webhookId, accessToken]);

  const name =
    profile?.name ||
    seedUser?.display_name ||
    [seedUser?.first_name, seedUser?.last_name].filter(Boolean).join(" ") ||
    "Bot";
  const avatarUser = {
    first_name: name,
    member_avatar_url:
      profile?.avatar_url || seedUser?.member_avatar_url || null,
  };
  const events = Array.isArray(profile?.recent_events)
    ? profile.recent_events
    : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Bot profile</DialogTitle>
        </DialogHeader>
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : (
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <OrgMemberAvatar
                user={avatarUser}
                className="h-14 w-14"
                fallbackClassName="text-base font-semibold"
              />
              <div className="min-w-0 flex-1">
                <p className="font-semibold text-sm truncate">{name}</p>
                <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <span
                    className={cn(
                      "h-2 w-2 rounded-full shrink-0",
                      BOT_PRESENCE.dot
                    )}
                    aria-hidden
                  />
                  {BOT_PRESENCE.label}
                </p>
                {profile?.channel_title ? (
                  <p className="text-xs text-muted-foreground mt-1">
                    Channel: {profile.channel_title}
                  </p>
                ) : null}
                {profile?.created_at ? (
                  <p className="text-xs text-muted-foreground mt-0.5">
                    Connected {formatWhen(profile.created_at)}
                  </p>
                ) : null}
                {profile?.revoked ? (
                  <p className="text-xs text-amber-600 mt-1">Revoked</p>
                ) : null}
              </div>
            </div>

            <div>
              <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-2">
                Recent events
              </p>
              {events.length === 0 ? (
                <p className="text-sm text-muted-foreground">
                  No recent posts from this bot yet.
                </p>
              ) : (
                <ul className="space-y-2">
                  {events.map((ev) => (
                    <li
                      key={ev.message_id}
                      className="rounded-md border border-border/80 px-2.5 py-2"
                    >
                      <p className="text-xs text-muted-foreground">
                        {formatWhen(ev.created_at)}
                      </p>
                      <p className="text-sm mt-0.5 whitespace-pre-wrap break-words">
                        {ev.content || "(empty)"}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
